import queryString from 'query-string'

import { stringify } from 'utils/json'
import { mergeDeepRight } from 'utils/object'
import { getCookie } from './cookie'

const CROSS_ORIGIN = ['3000'].includes(global.location?.port)

// default options for fetch request
// https://developer.mozilla.org/en-US/docs/Web/RestApi/Fetch_API/Using_Fetch#Supplying_request_options
const FETCH_BASE_REQUEST_OPTIONS = {
  method: 'GET',
  // Cross-Origin Resource Sharing (since it's a different port)
  // if serving the RestApi and the app on the same domain and port,
  // use 'same-origin' instead
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/mode
  mode: CROSS_ORIGIN ? 'cors' : 'same-origin',
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/cache
  cache: 'no-store',
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/credentials
  credentials: 'same-origin',
  redirect: 'error',
}

// Calculate difference in milliseconds between server and client clock
// getServerTime :: Response -> {serverTime: string, getServerTime: number}
export const getServerTime = response => {
  const serverTime = new Date(response.headers.get('date'))
  return {
    serverTime: serverTime.toISOString(),
    timeDelta: Math.trunc((serverTime.getTime() - Date.now()) / 1000),
  }
}

// Perform fetch return promise containing an object with either an
// `error` or `response` property. {error: Object} | {response: Object}
// Usage in redux saga generator
// const { response, error } = yield call(apiFetch, 'url')
//
// apiFetch :: (String, Object) -> Promise[{error|response}]
type ApiMeta = {
  url: string
  HTTPstatus: number
  serverTime: string
  timeDelta: number
}

// The body is JSON from the server, so its type is not known here
type ApiResult = { response?: unknown; error?: unknown; meta?: ApiMeta }

const apiFetch = (url: string, options = {}): Promise<ApiResult> => {
  return fetch(url, options)
    .then(response => {
      const json = response.json().catch(() => Promise.resolve({}))
      const meta = {
        url,
        HTTPstatus: response.status,
        ...getServerTime(response),
      }
      return json
        .then(data => (Array.isArray(data) ? { results: data } : data))
        .then(data => (response.ok ? { response: data } : { error: data }))
        .then(result => ({ ...result, meta }))
    })
    .catch(error => ({
      // fetch error – probably did not even get a http response from server.
      error: {
        message: error.message,
        url,
        options,
        stack: (error.stack ?? '').split(/\n+/),
      },
    }))
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

// Empty segments, including 0, are left out of the url
type RouteSegment = string | number | null | undefined

type ApiPayload = {
  formData?: FormData
} & Record<string, unknown>

// convenience wrapper for django-rest-framework RestApi methods
// returns an object containing promise returning functions for each http
// method. The `id` data value is always used for url routing.
// See apiFetch function for further details
//
// example usage:
// RestApi('https://example.com/RestApi').get('gizmos', {id: 42})
//
// RestApi :: (string, object?) -> {string: (string, {data}, string?) -> Promise}
// Typed as `any`, as it was while built with ramda. A real type here fails
// callers whose own types are wrong, such as `fetchCandidates`, which declares
// its query a string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RestApi = (urlRoot = '/api', extra_options = {}): any =>
  Object.fromEntries(
    HTTP_METHODS.map(method => [
      method.toLowerCase(),
      (route: RouteSegment | RouteSegment[], payload: ApiPayload = {}) => {
        const { formData, ...data } = payload
        const segments = [urlRoot, ...(Array.isArray(route) ? route : [route])]
        const path = [...segments.filter(Boolean), ''].join('/')
        const url = ['DELETE', 'GET'].includes(method)
          ? `${path}?${queryString.stringify(data, { arrayFormat: 'comma' })}`
          : path
        const body = method.startsWith('P') ? formData || stringify(data) : null
        const headers: Record<string, string> = {
          'X-CSRFToken': getCookie('csrftoken', 'NO TOKEN'),
        }
        if (!formData) headers['Content-Type'] = 'application/json'
        const options = [
          FETCH_BASE_REQUEST_OPTIONS,
          extra_options,
          { method, body, headers },
        ].reduce((merged, next) => mergeDeepRight(merged, next), {})
        return apiFetch(url, options)
      },
    ]),
  )

const API_URL = CROSS_ORIGIN
  ? 'http://localhost:8000/api' // cross origin in development
  : '/api' // same-origin

export default RestApi(API_URL)
